import React, { useEffect, useState } from 'react';
import { Dialog } from './Dialog';
import { Button } from './Button';
import salesOrderService from '../services/salesOrderService';
import { toBrazilDateTimeLocal } from '../utils/dateBrasilia';
import { SalesOrder } from '../types/salesOrder';

interface MarkDeliveredModalProps {
  isOpen: boolean;
  orderId: number;
  /** Identificador exibido no título (nº do pedido/ecommerce), opcional. */
  orderLabel?: string | number;
  onClose: () => void;
  /** Chamado após marcar como entregue com sucesso, com o pedido atualizado retornado pela API. */
  onConfirmed: (updated: SalesOrder) => void;
}

/**
 * Modal para marcar manualmente um pedido como Entregue (finance/admin). Coleta a data/hora de
 * entrega (input datetime-local, wall-clock de Brasília) e o motivo da atualização manual, que é
 * anexado à observação interna do pedido. Reutilizado na tela de detalhe do pedido e no relatório
 * de transporte.
 */
export const MarkDeliveredModal: React.FC<MarkDeliveredModalProps> = ({
  isOpen,
  orderId,
  orderLabel,
  onClose,
  onConfirmed,
}) => {
  const [deliveredAt, setDeliveredAt] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reinicia o formulário sempre que o modal abre, semeando a data/hora com "agora" (Brasília).
  useEffect(() => {
    if (isOpen) {
      setDeliveredAt(toBrazilDateTimeLocal());
      setReason('');
      setError(null);
      setSaving(false);
    }
  }, [isOpen]);

  const handleConfirm = async () => {
    setError(null);
    if (!deliveredAt) {
      setError('Informe a data e hora da entrega.');
      return;
    }
    if (!reason.trim()) {
      setError('Informe o motivo da atualização manual da entrega.');
      return;
    }
    setSaving(true);
    try {
      const updated = await salesOrderService.markDelivered(orderId, deliveredAt, reason.trim());
      onConfirmed(updated);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao marcar pedido como entregue');
    } finally {
      setSaving(false);
    }
  };

  const footer = (
    <div className="flex justify-end gap-3">
      <Button variant="outline" onClick={onClose} disabled={saving}>
        Cancelar
      </Button>
      <Button variant="success" onClick={handleConfirm} loading={saving}>
        Marcar como entregue
      </Button>
    </div>
  );

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={orderLabel ? `Marcar pedido ${orderLabel} como entregue` : 'Marcar pedido como entregue'}
      footer={footer}
      size="md"
      closeOnBackdrop={!saving}
      showCloseButton={!saving}
    >
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Data e hora da entrega
          </label>
          <input
            type="datetime-local"
            className="w-full input-field px-2 py-1.5 text-sm"
            value={deliveredAt}
            onChange={(e) => setDeliveredAt(e.target.value)}
            disabled={saving}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Motivo da atualização manual
          </label>
          <textarea
            className="w-full input-field px-2 py-1.5 text-sm"
            rows={3}
            maxLength={1000}
            placeholder="Descreva o motivo (será anexado à observação interna do pedido)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={saving}
          />
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}
      </div>
    </Dialog>
  );
};

export default MarkDeliveredModal;
